import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  Unsubscribe,
  increment
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
  Organization,
  MessagingCredits,
  CreditTransaction,
  CreditRequest,
  CreditPackage,
  CreditRequestStatus,
  MessagingActivationRequest,
  ActivationRequestStatus
} from '../types';

const CREDITS_COLLECTION = 'messagingCredits';
const CREDIT_TRANSACTIONS_COLLECTION = 'creditTransactions';
const CREDIT_REQUESTS_COLLECTION = 'creditRequests';
const CREDIT_PACKAGES_COLLECTION = 'creditPackages';
const ACTIVATION_REQUESTS_COLLECTION = 'messagingActivationRequests';

// ==================== CREDITS ====================

/**
 * Get messaging credits for an organization
 */
export const getMessagingCredits = async (
  organizationId: string
): Promise<MessagingCredits | null> => {
  try {
    const docRef = doc(db, CREDITS_COLLECTION, organizationId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return { id: docSnap.id, ...docSnap.data() } as MessagingCredits;
  } catch (error) {
    console.error('Error getting messaging credits:', error);
    throw error;
  }
};

/**
 * Subscribe to messaging credits (real-time updates)
 */
export const subscribeToCredits = (
  organizationId: string,
  callback: (credits: MessagingCredits | null) => void
): Unsubscribe => {
  const docRef = doc(db, CREDITS_COLLECTION, organizationId);

  return onSnapshot(docRef, (docSnap) => {
    if (!docSnap.exists()) {
      callback(null);
      return;
    }
    callback({ id: docSnap.id, ...docSnap.data() } as MessagingCredits);
  }, (error) => {
    console.error('Error in credits subscription:', error);
  });
};

// ==================== CREDIT TRANSACTIONS ====================

/**
 * Get credit transactions for an organization
 */
export const getCreditTransactions = async (
  organizationId: string,
  transactionLimit: number = 50
): Promise<CreditTransaction[]> => {
  try {
    const q = query(
      collection(db, CREDIT_TRANSACTIONS_COLLECTION),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc'),
      limit(transactionLimit)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as CreditTransaction);
  } catch (error) {
    console.error('Error getting credit transactions:', error);
    throw error;
  }
};

// ==================== CREDIT PACKAGES ====================

/**
 * Get all active credit packages (global, no org scoping)
 */
export const getCreditPackages = async (): Promise<CreditPackage[]> => {
  try {
    const q = query(
      collection(db, CREDIT_PACKAGES_COLLECTION),
      where('isActive', '==', true),
      orderBy('order', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as CreditPackage);
  } catch (error) {
    console.error('Error getting credit packages:', error);
    throw error;
  }
};

/**
 * Get all credit packages (including inactive, for admin)
 */
export const getAllCreditPackages = async (): Promise<CreditPackage[]> => {
  try {
    const q = query(
      collection(db, CREDIT_PACKAGES_COLLECTION),
      orderBy('order', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as CreditPackage);
  } catch (error) {
    console.error('Error getting all credit packages:', error);
    throw error;
  }
};

/**
 * Create a new credit package (admin only)
 */
export const createCreditPackage = async (
  pkg: Omit<CreditPackage, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  try {
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, CREDIT_PACKAGES_COLLECTION), {
      ...pkg,
      createdAt: now,
      updatedAt: now
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating credit package:', error);
    throw error;
  }
};

/**
 * Update a credit package (admin only)
 */
export const updateCreditPackage = async (
  packageId: string,
  updates: Partial<CreditPackage>
): Promise<void> => {
  try {
    const docRef = doc(db, CREDIT_PACKAGES_COLLECTION, packageId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error updating credit package:', error);
    throw error;
  }
};

// ==================== CREDIT REQUESTS ====================

/**
 * Create a credit request from an organization
 */
export const createCreditRequest = async (
  organizationId: string,
  organizationName: string,
  pkg: CreditPackage,
  requestedBy: { userId: string; name: string }
): Promise<string> => {
  try {
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, CREDIT_REQUESTS_COLLECTION), {
      organizationId,
      organizationName,
      packageId: pkg.id,
      creditAmount: pkg.creditAmount,
      price: pkg.price,
      currency: pkg.currency,
      status: 'pending' as CreditRequestStatus,
      requestedBy,
      createdAt: now,
      updatedAt: now
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating credit request:', error);
    throw error;
  }
};

/**
 * Get credit requests for an organization
 */
export const getCreditRequests = async (
  organizationId: string
): Promise<CreditRequest[]> => {
  try {
    const q = query(
      collection(db, CREDIT_REQUESTS_COLLECTION),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as CreditRequest);
  } catch (error) {
    console.error('Error getting credit requests:', error);
    throw error;
  }
};

/**
 * Subscribe to credit requests for an organization (real-time)
 */
export const subscribeToCreditRequests = (
  organizationId: string,
  callback: (requests: CreditRequest[]) => void
): Unsubscribe => {
  const q = query(
    collection(db, CREDIT_REQUESTS_COLLECTION),
    where('organizationId', '==', organizationId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const requests = snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as CreditRequest);
    callback(requests);
  }, (error) => {
    console.error('Error in credit requests subscription:', error);
  });
};

// ==================== ADMIN FUNCTIONS ====================

/**
 * Get all pending credit requests across all orgs (platform admin)
 */
export const getAllPendingCreditRequests = async (): Promise<CreditRequest[]> => {
  try {
    const q = query(
      collection(db, CREDIT_REQUESTS_COLLECTION),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as CreditRequest);
  } catch (error) {
    console.error('Error getting pending credit requests:', error);
    throw error;
  }
};

/**
 * Get all credit requests (platform admin)
 */
export const getAllCreditRequests = async (): Promise<CreditRequest[]> => {
  try {
    const q = query(
      collection(db, CREDIT_REQUESTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as CreditRequest);
  } catch (error) {
    console.error('Error getting all credit requests:', error);
    throw error;
  }
};

/**
 * Get all org credits (platform admin overview)
 */
export const getAllMessagingCredits = async (): Promise<MessagingCredits[]> => {
  try {
    const snapshot = await getDocs(collection(db, CREDITS_COLLECTION));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as MessagingCredits);
  } catch (error) {
    console.error('Error getting all messaging credits:', error);
    throw error;
  }
};

/**
 * Fulfill a credit request via Cloud Function (atomically adds credits)
 */
export const fulfillCreditRequest = async (
  requestId: string,
  reviewedBy: { userId: string; name: string }
): Promise<{ success: boolean }> => {
  try {
    const callable = httpsCallable(functions, 'fulfillCreditRequest');
    const result = await callable({ requestId, reviewedBy });
    return result.data as { success: boolean };
  } catch (error: any) {
    console.error('Error fulfilling credit request:', error);
    throw error;
  }
};

/**
 * Reject a credit request
 */
export const rejectCreditRequest = async (
  requestId: string,
  reason: string,
  reviewedBy: { userId: string; name: string }
): Promise<void> => {
  try {
    const docRef = doc(db, CREDIT_REQUESTS_COLLECTION, requestId);
    await updateDoc(docRef, {
      status: 'rejected' as CreditRequestStatus,
      rejectionReason: reason,
      reviewedBy,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error rejecting credit request:', error);
    throw error;
  }
};

/**
 * Manual credit top-up via Cloud Function (platform admin)
 */
export const manualTopUp = async (
  organizationId: string,
  amount: number,
  performedBy: { userId: string; name: string }
): Promise<{ success: boolean }> => {
  try {
    const callable = httpsCallable(functions, 'manualCreditTopUp');
    const result = await callable({ organizationId, amount, performedBy });
    return result.data as { success: boolean };
  } catch (error: any) {
    console.error('Error performing manual top-up:', error);
    throw error;
  }
};

/**
 * Create Twilio subaccount for an organization (platform admin)
 */
export const createTwilioSubaccount = async (
  organizationId: string,
  friendlyName: string
): Promise<{ success: boolean; subaccountSid?: string }> => {
  try {
    const callable = httpsCallable(functions, 'createTwilioSubaccount');
    const result = await callable({ organizationId, friendlyName });
    return result.data as { success: boolean; subaccountSid?: string };
  } catch (error: any) {
    console.error('Error creating Twilio subaccount:', error);
    throw error;
  }
};

// ==================== SELF-SERVICE ACTIVATION ====================

/**
 * Activate messaging for testing (self-service).
 * Creates Twilio subaccount server-side, saves contact info, gives 10 test credits.
 */
export const activateMessagingForTesting = async (
  organizationId: string,
  organizationName: string,
  contactPhone: string,
  contactEmail: string,
  country?: string,
  address?: string
): Promise<{ success: boolean; subaccountSid?: string }> => {
  try {
    const callable = httpsCallable(functions, 'activateMessagingForTesting');
    const result = await callable({
      organizationId,
      organizationName,
      contactPhone,
      contactEmail,
      ...(country && { country }),
      ...(address && { address })
    });
    return result.data as { success: boolean; subaccountSid?: string };
  } catch (error: any) {
    console.error('Error activating messaging for testing:', error);
    throw error;
  }
};

// ==================== ACTIVATION REQUESTS (ADMIN) ====================

/**
 * Get all activation requests (platform admin)
 */
export const getAllActivationRequests = async (): Promise<MessagingActivationRequest[]> => {
  try {
    const q = query(
      collection(db, ACTIVATION_REQUESTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as MessagingActivationRequest);
  } catch (error) {
    console.error('Error getting activation requests:', error);
    throw error;
  }
};

/**
 * Approve an activation request: create Twilio subaccount + update request status
 */
export const approveActivationRequest = async (
  requestId: string,
  orgId: string,
  friendlyName: string,
  reviewedBy: { userId: string; name: string }
): Promise<void> => {
  try {
    await createTwilioSubaccount(orgId, friendlyName);
    const docRef = doc(db, ACTIVATION_REQUESTS_COLLECTION, requestId);
    await updateDoc(docRef, {
      status: 'approved' as ActivationRequestStatus,
      reviewedBy,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error approving activation request:', error);
    throw error;
  }
};

/**
 * Reject an activation request
 */
export const rejectActivationRequest = async (
  requestId: string,
  reason: string,
  reviewedBy: { userId: string; name: string }
): Promise<void> => {
  try {
    const docRef = doc(db, ACTIVATION_REQUESTS_COLLECTION, requestId);
    await updateDoc(docRef, {
      status: 'rejected' as ActivationRequestStatus,
      rejectionReason: reason,
      reviewedBy,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error rejecting activation request:', error);
    throw error;
  }
};

// ==================== TEST TOP-UP (DEV ONLY) ====================

/**
 * Test top-up: directly writes credits to Firestore (bypasses admin Cloud Function).
 * For development/testing only.
 */
export const testTopUp = async (
  organizationId: string,
  amount: number,
  performedBy: { userId: string; name: string }
): Promise<void> => {
  try {
    const creditsRef = doc(db, CREDITS_COLLECTION, organizationId);

    // Read current balance for the audit record
    const snap = await getDoc(creditsRef);
    const currentBalance = snap.exists() ? (snap.data().messageCredits ?? 0) : 0;

    // Increment credits (creates doc if missing)
    await setDoc(creditsRef, {
      organizationId,
      messageCredits: increment(amount),
      lastTopUpAt: Timestamp.now(),
      lastTopUpAmount: amount,
      updatedAt: Timestamp.now()
    }, { merge: true });

    // Write audit transaction
    await addDoc(collection(db, CREDIT_TRANSACTIONS_COLLECTION), {
      organizationId,
      type: 'topup',
      amount,
      balanceBefore: currentBalance,
      balanceAfter: currentBalance + amount,
      description: `Test top-up: +${amount} credits`,
      performedBy,
      createdAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error performing test top-up:', error);
    throw error;
  }
};

// ==================== ORGANIZATION HELPERS ====================

/**
 * Get all organizations (platform admin)
 */
export const getAllOrganizations = async (): Promise<Organization[]> => {
  try {
    const snapshot = await getDocs(collection(db, 'organizations'));
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as Organization);
  } catch (error) {
    console.error('Error getting all organizations:', error);
    throw error;
  }
};
